import { useAuth } from "../context/AuthContext.js";
import { useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../api/api.js";
import MainLayout from "../sidebar/Sidebar.js";
import { motion, AnimatePresence } from "framer-motion";
import { Receipt, FileText, CheckCircle, Clock } from "lucide-react";
import { SEARCH_QUERY_KEYS } from "../hooks/useSearchInvalidation.js";

export default function ReceiptsList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [location, setLocation] = useState("Receipts");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [isFiltering, setIsFiltering] = useState(false);

  function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);

      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);

    return debouncedValue;
  }
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const {
    data: receipts = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: SEARCH_QUERY_KEYS.receipts(user?.uid),
    queryFn: async () => {
      const response = await api.get("/receipts");
      return response.data;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    cacheTime: 10 * 60 * 1000,
  });

  const filteredReceipts = useMemo(() => {
    let filtered = receipts;

    if (debouncedSearchQuery) {
      filtered = filtered.filter((receipt) =>
        receipt.original_name
          .toLowerCase()
          .includes(debouncedSearchQuery.toLowerCase())
      );
    }

    if (statusFilter !== "All") {
      const status = statusFilter.toLowerCase();
      filtered = filtered.filter(
        (receipt) => receipt.processing_status === status
      );
    }

    return filtered;
  }, [receipts, debouncedSearchQuery, statusFilter]);

  const metrics = useMemo(() => {
    const total = receipts.length;
    const processed = receipts.filter(
      (r) => r.processing_status === "processed"
    ).length;
    const pending = receipts.filter(
      (r) => r.processing_status === "pending"
    ).length;
    const failed = receipts.filter(
      (r) => r.processing_status === "failed"
    ).length;

    return { total, processed, pending, failed };
  }, [receipts]);

  const handleStatusFilter = (status) => {
    setIsFiltering(true);
    setStatusFilter(status);
    setTimeout(() => setIsFiltering(false), 200);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    if (e.target.value !== debouncedSearchQuery) {
      setIsFiltering(true);
      setTimeout(() => setIsFiltering(false), 350);
    }
  };

  return (
    <MainLayout location={location} setLocation={setLocation}>
      <div className="bg-gray-50 min-h-full py-4 px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {/* Total Receipts */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Receipt className="w-5 h-5 text-gray-600" />
                </div>
                <span className="text-sm text-gray-900">Total Receipts</span>
              </div>
              <div className="text-3xl font-light text-gray-900">
                {metrics.total}
              </div>
              <div className="text-sm text-gray-500">All uploaded receipts</div>
            </div>

            {/* Processed */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-gray-600" />
                </div>
                <span className="text-sm text-gray-900">Processed</span>
              </div>
              <div className="text-3xl font-light text-gray-900">
                {metrics.processed}
              </div>
              <div className="text-sm text-gray-500">
                {metrics.total > 0
                  ? Math.round((metrics.processed / metrics.total) * 100)
                  : 0}
                % of total
              </div>
            </div>

            {/* Pending */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Clock className="w-5 h-5 text-gray-600" />
                </div>
                <span className="text-sm text-gray-900">Pending</span>
              </div>
              <div className="text-3xl font-light text-gray-900">
                {metrics.pending}
              </div>
              <div className="text-sm text-gray-500">Awaiting processing</div>
            </div>

            {/* Failed */}
            <div className="bg-white p-4 rounded-lg border border-gray-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <FileText className="w-5 h-5 text-gray-600" />
                </div>
                <span className="text-sm text-gray-900">Failed</span>
              </div>
              <div className="text-3xl font-light text-gray-900">
                {metrics.failed}
              </div>
              <div className="text-sm text-gray-500">Processing errors</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
            <div className="flex gap-2">
              {["All", "Processed", "Pending"].map((status) => (
                <button
                  key={status}
                  onClick={() => handleStatusFilter(status)}
                  className={`px-3 py-2 rounded-md border text-sm shadow-sm transition flex items-center gap-2 ${
                    statusFilter === status
                      ? "border-gray-300 bg-gray-100 text-gray-900"
                      : "border-gray-300 text-gray-700 bg-white hover:bg-gray-50"
                  }`}
                >
                  {isFiltering && statusFilter === status && (
                    <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  )}
                  {status}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search receipts..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="bg-white text-gray-900 placeholder-gray-400 rounded-md px-4 py-2 pr-10 border border-gray-300 shadow-sm max-w-xs w-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {isFiltering && searchQuery && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {isLoading && (
            <div className="text-gray-500 text-center flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              Loading receipts...
            </div>
          )}

          {isFiltering && !isLoading && (
            <div className="text-gray-500 text-center flex items-center justify-center gap-2 mb-4">
              <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              Filtering receipts...
            </div>
          )}

          {error && (
            <div className="text-red-600 text-center">
              Failed to load receipts. Please try again.
            </div>
          )}

          {!isLoading &&
            !error &&
            filteredReceipts.length === 0 &&
            !isFiltering && (
              <div className="text-gray-500 text-center">
                {searchQuery || statusFilter !== "All"
                  ? "No receipts match your filters."
                  : "No receipts found."}
              </div>
            )}

          <AnimatePresence mode="wait">
            <motion.div
              key={`${statusFilter}-${debouncedSearchQuery}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filteredReceipts.map((receipt) => (
                <motion.div
                  key={receipt.id}
                  layoutId={`receipt-${receipt.id}`}
                  onClick={() => navigate(`/receipts/${receipt.id}`)}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 hover:border-gray-300 hover:shadow-md cursor-pointer transition-all duration-200"
                >
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-medium text-lg text-gray-900">
                        {receipt.original_name}
                      </h3>
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded-full border ${
                          receipt.processing_status === "processed"
                            ? "bg-blue-100 text-blue-800 border-blue-200"
                            : receipt.processing_status === "pending"
                            ? "bg-blue-100 text-blue-800 border-blue-200"
                            : "bg-blue-100 text-blue-800 border-blue-200"
                        }`}
                      >
                        {receipt.processing_status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mb-6">
                      {`Uploaded: ${new Date(
                        receipt.created_at
                      ).toLocaleDateString()}`}
                    </p>
                    <div className="flex justify-between text-sm text-gray-600 mb-4">
                      <div>
                        <p className="text-gray-500 text-xs mb-1">File Size</p>
                        <p className="font-medium text-gray-900">
                          {(receipt.file_size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-xs mb-1">
                          Content Type
                        </p>
                        <p className="font-medium text-gray-900">
                          {receipt.content_type}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-between px-6 pb-6 pt-0">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/receipts/${receipt.id}`);
                      }}
                      className="border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 px-4 py-2 rounded-md text-sm shadow-sm transition"
                    >
                      View Details
                    </button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
      </div>
    </MainLayout>
  );
}

// Old Version
// import { useAuth } from "../context/AuthContext.js";
// import { useNavigate } from "react-router-dom";
// import { useState, useMemo, useEffect } from "react";
// import { useQuery } from "@tanstack/react-query";
// import api from "../api/api.js";
// import MainLayout from "../sidebar/Sidebar.js";
// import { motion, AnimatePresence } from "framer-motion";

// export default function ReceiptsList() {
//   const { user } = useAuth();
//   const navigate = useNavigate();
//   const [location, setLocation] = useState("Receipts");
//   const [searchQuery, setSearchQuery] = useState("");
//   const [statusFilter, setStatusFilter] = useState("All");
//   const [isFiltering, setIsFiltering] = useState(false);

//   function useDebounce(value, delay) {
//     const [debouncedValue, setDebouncedValue] = useState(value);

//     useEffect(() => {
//       const handler = setTimeout(() => {
//         setDebouncedValue(value);
//       }, delay);

//       return () => {
//         clearTimeout(handler);
//       };
//     }, [value, delay]);

//     return debouncedValue;
//   }
//   const debouncedSearchQuery = useDebounce(searchQuery, 300);

//   const {
//     data: receipts = [],
//     isLoading,
//     error,
//   } = useQuery({
//     queryKey: ["receipts", user],
//     queryFn: async () => {
//       const response = await api.get("/receipts");
//       return response.data;
//     },
//     enabled: !!user,
//     staleTime: 5 * 60 * 1000, // 5 minutes
//     cacheTime: 10 * 60 * 1000, // 10 minutes
//   });

//   const filteredReceipts = useMemo(() => {
//     let filtered = receipts;

//     // Filter by search query
//     if (debouncedSearchQuery) {
//       filtered = filtered.filter((receipt) =>
//         receipt.original_name
//           .toLowerCase()
//           .includes(debouncedSearchQuery.toLowerCase())
//       );
//     }

//     // Filter by status
//     if (statusFilter !== "All") {
//       const status = statusFilter.toLowerCase();
//       filtered = filtered.filter(
//         (receipt) => receipt.processing_status === status
//       );
//     }

//     return filtered;
//   }, [receipts, debouncedSearchQuery, statusFilter]);

//   const handleStatusFilter = (status) => {
//     setIsFiltering(true);
//     setStatusFilter(status);
//     setTimeout(() => setIsFiltering(false), 200);
//   };

//   const handleSearchChange = (e) => {
//     setSearchQuery(e.target.value);
//     if (e.target.value !== debouncedSearchQuery) {
//       setIsFiltering(true);
//       setTimeout(() => setIsFiltering(false), 350);
//     }
//   };

//   return (
//     <MainLayout location={location} setLocation={setLocation}>
//       <div className="bg-gray-50 py-6">
//         <div className="max-w-6xl mx-auto">
//           {/* Filters and Search */}
//           <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
//             <div className="flex gap-3">
//               {["All", "Processed", "Pending"].map((status) => (
//                 <button
//                   key={status}
//                   onClick={() => handleStatusFilter(status)}
//                   className={`px-4 py-2 rounded-full border transition flex items-center gap-2 ${
//                     statusFilter === status
//                       ? "border-green-500 bg-green-50 text-green-700"
//                       : "border-gray-300 text-gray-700 hover:bg-gray-100"
//                   }`}
//                 >
//                   {isFiltering && statusFilter === status && (
//                     <div className="w-3 h-3 border border-green-500 border-t-transparent rounded-full animate-spin" />
//                   )}
//                   {status}
//                 </button>
//               ))}
//             </div>
//             <div className="flex items-center gap-3">
//               <div className="relative">
//                 <input
//                   type="text"
//                   placeholder="Search receipts..."
//                   value={searchQuery}
//                   onChange={handleSearchChange}
//                   className="bg-gray-100 text-gray-900 placeholder-gray-400 rounded-md px-4 py-2 pr-10 max-w-xs w-full focus:outline-none focus:ring-2 focus:ring-green-500"
//                 />
//                 {isFiltering && searchQuery && (
//                   <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
//                     <div className="w-4 h-4 border border-green-500 border-t-transparent rounded-full animate-spin" />
//                   </div>
//                 )}
//               </div>
//             </div>
//           </div>

//           {isLoading && (
//             <div className="text-gray-500 text-center flex items-center justify-center gap-2">
//               <div className="w-5 h-5 border border-green-500 border-t-transparent rounded-full animate-spin" />
//               Loading receipts...
//             </div>
//           )}

//           {isFiltering && !isLoading && (
//             <div className="text-gray-500 text-center flex items-center justify-center gap-2 mb-4">
//               <div className="w-4 h-4 border border-green-500 border-t-transparent rounded-full animate-spin" />
//               Filtering receipts...
//             </div>
//           )}

//           {error && (
//             <div className="text-red-600 text-center">
//               Failed to load receipts. Please try again.
//             </div>
//           )}

//           {!isLoading &&
//             !error &&
//             filteredReceipts.length === 0 &&
//             !isFiltering && (
//               <div className="text-gray-500 text-center">
//                 {searchQuery || statusFilter !== "All"
//                   ? "No receipts match your filters."
//                   : "No receipts found."}
//               </div>
//             )}

//           <AnimatePresence mode="wait">
//             <motion.div
//               key={`${statusFilter}-${debouncedSearchQuery}`}
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               exit={{ opacity: 0, y: -20 }}
//               transition={{ duration: 0.2, ease: "easeInOut" }}
//               className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
//             >
//               {filteredReceipts.map((receipt) => (
//                 <motion.div
//                   key={receipt.id}
//                   layoutId={`receipt-${receipt.id}`}
//                   onClick={() => navigate(`/receipts/${receipt.id}`)}
//                   className="bg-white rounded-2xl shadow-md border border-gray-200 hover:border-green-500 hover:scale-105 hover:shadow-lg cursor-pointer transition-all duration-200"
//                 >
//                   <div className="p-8">
//                     <div className="flex justify-between items-center mb-3">
//                       <h3 className="font-bold text-xl text-gray-900">
//                         {receipt.original_name}
//                       </h3>
//                       <span
//                         className={`text-sm font-semibold px-3 py-1 rounded-full border ${
//                           receipt.processing_status === "processed"
//                             ? "bg-green-100 text-green-700 border-green-300"
//                             : "bg-yellow-100 text-yellow-700 border-yellow-300"
//                         }`}
//                       >
//                         {receipt.processing_status}
//                       </span>
//                     </div>
//                     <p className="text-base text-gray-600 mb-8">
//                       {`Uploaded: ${new Date(
//                         receipt.created_at
//                       ).toLocaleDateString()}`}
//                     </p>
//                     <div className="flex justify-between text-base text-gray-600 mb-3">
//                       <div>
//                         <p className="text-gray-400">File Size</p>
//                         <p className="font-medium">
//                           {(receipt.file_size / 1024).toFixed(2)} KB
//                         </p>
//                       </div>
//                       <div>
//                         <p className="text-gray-400">Content Type</p>
//                         <p className="font-medium">{receipt.content_type}</p>
//                       </div>
//                     </div>
//                   </div>
//                   <div className="flex justify-between px-8 pb-8 pt-0">
//                     <button
//                       onClick={(e) => {
//                         e.stopPropagation();
//                         navigate(`/receipts/${receipt.id}`);
//                       }}
//                       className="border border-green-600 text-green-600 hover:bg-green-600 hover:text-white px-5 py-3 rounded-lg text-base transition"
//                     >
//                       Details
//                     </button>
//                   </div>
//                 </motion.div>
//               ))}
//             </motion.div>
//           </AnimatePresence>
//         </div>
//       </div>
//     </MainLayout>
//   );
// }
